import React from 'react';
import { ShieldAlert, Layers, Lock, Home } from 'lucide-react';
import { MODULE_META_CATALOG } from '../shared/meta';

interface AccessDeniedPageProps {
  moduleName?: string;
  userRole?: string;
  onNavigate: (tabId: string) => void;
}

export const AccessDeniedPage: React.FC<AccessDeniedPageProps> = ({
  moduleName,
  userRole,
  onNavigate,
}) => {
  const meta = moduleName ? MODULE_META_CATALOG[moduleName] : undefined;
  const friendlyName = meta ? meta.title : moduleName ? moduleName.replace(/_/g, ' ').toUpperCase() : 'Restricted Resource';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '75vh',
        padding: '40px 24px',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: '620px',
          width: '100%',
          background: '#ffffff',
          borderRadius: '24px',
          border: '1px solid #fee2e2',
          boxShadow: '0 20px 45px -15px rgba(239, 68, 68, 0.12)',
          padding: '48px 36px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Accent Bar */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #ef4444 0%, #f97316 100%)',
          }}
        />

        {/* Shield Icon Badge */}
        <div
          style={{
            width: '84px',
            height: '84px',
            borderRadius: '50%',
            background: '#fef2f2',
            border: '2px dashed #fca5a5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            color: '#dc2626',
            boxShadow: '0 8px 16px rgba(220, 38, 38, 0.10)',
          }}
        >
          <ShieldAlert size={42} strokeWidth={1.75} />
        </div>

        {/* Status Badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '5px 14px',
            borderRadius: '999px',
            background: '#fee2e2',
            color: '#991b1b',
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            marginBottom: '16px',
          }}
        >
          <Lock size={12} /> 403 Forbidden — Access Denied
        </div>

        <h1
          style={{
            fontSize: '23px',
            fontWeight: 800,
            color: '#0f172a',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
          }}
        >
          You Do Not Have Permission to Access This Module
        </h1>

        <p
          style={{
            fontSize: '14.5px',
            lineHeight: 1.6,
            color: '#64748b',
            margin: '0 0 16px 0',
            maxWidth: '520px',
          }}
        >
          Access to <strong>"{friendlyName}"</strong> is restricted. Your current user role (
          <code
            style={{
              background: '#f1f5f9',
              padding: '2px 8px',
              borderRadius: '6px',
              color: '#334155',
              fontWeight: 700,
              fontSize: '13px',
            }}
          >
            {userRole || 'Unassigned Role'}
          </code>
          ) does not have authorization to view or perform actions in this domain.
        </p>

        {moduleName && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              color: '#64748b',
              marginBottom: '20px',
            }}
          >
            <span>Direct URL Attempt:</span>
            <code style={{ color: '#0f172a', fontWeight: 600 }}>?tab={moduleName}</code>
          </div>
        )}

        <p
          style={{
            fontSize: '13px',
            lineHeight: 1.5,
            color: '#94a3b8',
            margin: '0 0 28px 0',
            maxWidth: '480px',
          }}
        >
          If you believe you require access to this operational area to fulfill your organizational duties, please contact your Organization Safety Administrator or System Administrator.
        </p>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => onNavigate('dashboard')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '11px 22px',
              borderRadius: '12px',
              background: '#2563eb',
              color: '#ffffff',
              fontSize: '13.5px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              transition: 'background 0.2s',
            }}
          >
            <Home size={16} /> Return to Dashboard
          </button>

          <button
            type="button"
            onClick={() => onNavigate('modules')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '11px 22px',
              borderRadius: '12px',
              background: '#f1f5f9',
              color: '#334155',
              fontSize: '13.5px',
              fontWeight: 600,
              border: '1px solid #cbd5e1',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
          >
            <Layers size={16} /> View Allowed Modules
          </button>
        </div>
      </div>
    </div>
  );
};
